import { ROUTER } from '@/app-constants/router.constant';
import { RouteLocationNormalized } from 'vue-router';
import { AuthUser } from '@/entities/authUser.entity';
import {
  hasAccess,
  hasMultitenancyAccess,
  isAdministrator,
  isCustomerSuccessManager,
  isPartnerTenant,
} from '@/utils/user.utils';
import { AccessPolicies, AccessPolicy } from '@/entities/role.entity';
import { Tenant } from '@/entities/tenant.entity';

const validateAdministratorRoutes = (to: RouteLocationNormalized, user: AuthUser): string => {
  const canAccess = hasAccess(user, to.meta?.accessPolicy as AccessPolicy | AccessPolicies);

  if (!canAccess) {
    return ROUTER.ADMIN.HOME_PAGE.PATH;
  }

  return to.path;
};

const validateCSMRoutes = (to: RouteLocationNormalized, user: AuthUser): string | boolean => {
  const canAccess = hasAccess(user, to.meta?.accessPolicy as AccessPolicy | AccessPolicies);

  if (!canAccess) {
    return ROUTER.ADMIN.HOME_PAGE.PATH;
  }

  if (to.path === ROUTER.ADMIN.CSM_PAGE.PATH || to.path === ROUTER.ADMIN.CSM_MANAGED_CUSTOMERS_PAGE.PATH) {
    return ROUTER.ADMIN.HOME_PAGE.PATH;
  }

  return true;
};

const validateCustomerRoutes = async (
  to: RouteLocationNormalized,
  user: AuthUser,
  partnerSelectedTenant?: Tenant,
): Promise<string | boolean> => {
  // if user has access to partner landing page, the path must start with /partner
  if (
    (hasAccess(user, ROUTER.PARTNER.LANDING_PAGE.accessPolicy) || isPartnerTenant(user)) &&
    !to.path.startsWith(ROUTER.PARTNER.LANDING_PAGE.PATH)
  ) {
    // check if user has selected a tenant, if YES then redirect the user to the tenant's home
    if (partnerSelectedTenant) {
      return ROUTER.PARTNER.CUSTOMER.HOME_PAGE.PATH.replace(':tenantId', partnerSelectedTenant.id);
    }
    return ROUTER.PARTNER.LANDING_PAGE.PATH;
  }

  // check if the page is protected
  if (to.meta?.accessPolicy && !hasMultitenancyAccess(user, to.meta?.accessPolicy as AccessPolicy)) {
    if (hasAccess(user, ROUTER.PARTNER.LANDING_PAGE.accessPolicy) || isPartnerTenant(user)) {
      return ROUTER.PARTNER.LANDING_PAGE.PATH;
    } else {
      return ROUTER.CUSTOMER.HOME_PAGE.PATH;
    }
  }

  return true;
};

export const validateNextRoute = async (
  to: RouteLocationNormalized,
  user: AuthUser,
  partnerSelectedTenant?: Tenant,
): Promise<string | boolean> => {
  const isAdmin = isAdministrator(user);
  const isCSM = isCustomerSuccessManager(user);

  if (isAdmin) {
    return validateAdministratorRoutes(to, user);
  } else if (isCSM) {
    return validateCSMRoutes(to, user);
  } else {
    // If the next route is a dashboard route, check if the user has the necessary products
    return await validateCustomerRoutes(to, user, partnerSelectedTenant);
  }
};
